import { Grid } from "@material-ui/core";
import Dialog from "components/Dialog";
import React from "react";
import TextFieldwithOptions from "components/TextField/TextFieldwithOptions";
import useText from "constants/text";
import { AddRequestInput, EDays, EReccurenceType, ReccurenceOptionsInput } from "generated/graphql";
import { ErrorStep } from "../MainStyles";
import { getRecurrenceMonthOptions, isFieldWithValue, isNormalInteger } from "../utils";
import DayOptionReccurence from "./DayOptionReccurence";
import MonthOptionReccurence from "./MonthOptionReccurence";
import { customReccurenceOptions } from ".";


interface IReccurenceModal {
  open: boolean;
  onCancel: () => void;
  onValidateCustomization: (reccurenceType: EReccurenceType, options: ReccurenceOptionsInput) => void;
  daysOptions?: Array<String>;
  startRequestDate?: string;
  reccurence: AddRequestInput;
}

export default function ReccurenceModal({
  open,
  onCancel,
  onValidateCustomization,
  daysOptions = [],
  startRequestDate = "",
  reccurence,
}: IReccurenceModal) {
  const { text } = useText();
  const [reccurenceOptionSelected, setReccurenceOptionSelected] = React.useState(
    reccurence?.reccurenceType || EReccurenceType.Weeks
  );
  const [reccurenceOptionMonthSelected, setReccurenceOptionMonthSelected] = React.useState(
    reccurence?.reccurenceOptions
  );
  const [reccurenceValue, setReccurenceValue] = React.useState(reccurence?.reccurenceOptions?.each || 1);

  const [defaultDaysOptions] = React.useState([
    {
      libelle: text.requestQuote.reccurence_day_option_monday,
      value: EDays.Monday,
    },
    { libelle: text.requestQuote.reccurence_day_option_tuesday, value: EDays.Tuesday },
    {
      libelle: text.requestQuote.reccurence_day_option_wednesday,
      value: EDays.Wednesday,
    },
    {
      libelle: text.requestQuote.reccurence_day_option_thursday,
      value: EDays.Thursday,
    },
    { libelle: text.requestQuote.reccurence_day_option_friday, value: EDays.Friday },
    {
      libelle: text.requestQuote.reccurence_day_option_saturday,
      value: EDays.Saturday,
    },
  ]);

  const [selectedDays, setSelectedDays] = React.useState(reccurence?.reccurenceOptions?.days || []);
  const [error, setError] = React.useState(false);

  const optionsMonths = getRecurrenceMonthOptions(startRequestDate);

  React.useEffect(() => setError(false), [reccurenceOptionSelected]);

  const optionsReccurence = [
    {
      label: text.requestQuote.reccurence_type_days,
      value: EReccurenceType.Days,
    },
    {
      label: text.requestQuote.reccurence_type_weeks,
      value: EReccurenceType.Weeks,
    },
    {
      label: text.requestQuote.reccurence_type_months,
      value: EReccurenceType.Months,
    },
    {
      label: text.requestQuote.reccurence_type_year,
      value: EReccurenceType.Years,
    },
  ];

  const renderReccurenceSubOptions = React.useMemo(() => {
    switch (reccurenceOptionSelected) {
      case EReccurenceType.Weeks:
        return (
          <DayOptionReccurence
            dayOptions={defaultDaysOptions}
            selectedDays={selectedDays}
            setSelectedDays={setSelectedDays}
          />
        );
      case EReccurenceType.Months:
        return (
          <MonthOptionReccurence
            options={optionsMonths}
            option={reccurenceOptionMonthSelected}
            setReccurenceOptionMonth={setReccurenceOptionMonthSelected}
            error={error}
          />
        );
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reccurenceOptionSelected, reccurenceOptionMonthSelected, error]);

  const handleConfirm = () => {
    if (reccurenceValue > 0 && customReccurenceOptions.includes(reccurenceOptionSelected)) {
      switch (reccurenceOptionSelected) {
        case EReccurenceType.Weeks:
          if (selectedDays?.length > 0) {
            const options: ReccurenceOptionsInput = {
              each: reccurenceValue,
              days: selectedDays,
            };
            onValidateCustomization(EReccurenceType.Weeks, options);
          } else {
            setError(true);
          }
          break;
        case EReccurenceType.Months:
          if (isFieldWithValue(reccurenceOptionMonthSelected?.value?.toString())) {
            const options: ReccurenceOptionsInput = {
              each: reccurenceValue,
              value: reccurenceOptionMonthSelected?.value,
            };
            onValidateCustomization(EReccurenceType.Months, options);
          } else {
            setError(true);
          }
          break;
        default:
          const options: ReccurenceOptionsInput = {
            each: reccurenceValue,
          };
          onValidateCustomization(reccurenceOptionSelected, options);
          break;
      }
    } else {
      setError(true);
    }
  };

  const handleClose = () => {
    onCancel();
  };

  const onChangeReccurenceValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isFieldWithValue(event.target.value as string)) {
      if (isNormalInteger(event.target.value as string)) {
        setReccurenceValue(Number(event.target.value as string));
      }
    } else {
      setReccurenceValue(null);
    }
  };

  const handleOptionChange = (reccurenceOptionSelected: string) => {
    setReccurenceOptionSelected(reccurenceOptionSelected as EReccurenceType);
  };

  return (
    <Dialog
      title={text.requestQuote.reccurence_modal_name}
      open={open}
      handleClose={handleClose}
      handleConfirm={handleConfirm}
    >
      <Grid container spacing={4} style={{ padding: "1rem" }}>
        <Grid item xs={12}>
          <TextFieldwithOptions
            name="textfieldoptions"
            fullWidth
            label={text.requestQuote.reccurence_value_label}
            placeholder={text.requestQuote.placeholder_reccurence}
            options={optionsReccurence}
            reccurenceOptionValue={reccurenceOptionSelected}
            errorOptionValue={error}
            textFieldValue={reccurenceValue || ""}
            value={reccurenceValue}
            error={error && !reccurenceValue}
            helperText={error && !reccurenceValue ? "" : ""}
            onChangeInputValue={onChangeReccurenceValue}
            handleOptionChange={handleOptionChange}
          />
        </Grid>
        <Grid item xs={12}>
          {renderReccurenceSubOptions}
          {error && !customReccurenceOptions.includes(reccurenceOptionSelected) && <ErrorStep>{text.requestQuote.reccurence_option_error}</ErrorStep>}
          {error && selectedDays?.length === 0 && reccurenceOptionSelected === EReccurenceType.Weeks && (
            <ErrorStep>{text.requestQuote.reccurence_day_error}</ErrorStep>
          )}
        </Grid>
      </Grid>
    </Dialog>
  );
}
